import {
    faAngleDown,
    faArrowLeft,
    faBars,
    faBorderAll,
    faRefresh,
    faSparkle,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Dropdown } from "react-bootstrap";
import { getGlobalStates } from "userful-chronos-app-common-js/dist/globalstates/globalStates";
import { StringID } from "userful-chronos-app-common-js/dist/models/common";
import { GcmUserPermission } from "userful-chronos-app-common-js/dist/models/gcm-user-permission";
import { Button, UserfulSearchBar, UserfulTooltip } from "userful-chronos-common-ui/dist";
import { ClickAndDragZone } from "userful-chronos-common-ui/dist/elements";
import EmptyPlaceholder from "../../../common/components/placeholders/EmptyPlaceholder";
import { convertIClusterInfoToTable } from "../../../common/components/UserfulTable/UserfulTableConverter";
import UserfulTable from "../../../common/Table2026/UserfulTable2026";
import { apiGetConstellations } from "../../../messages/api/gcmAPICallers";
import { ClusterRemoteManagement, IClusterInfo, IConstellationInfo } from "../../../model/CarbonExternalModels";
import { AppSliceActions } from "../../../store/app/appSlice";
import {
    selectClusterFilters,
    selectClusterView,
    selectSearchInput,
    useCarbonDispatch,
    useCarbonSelector,
} from "../../../store/carbonStore";
import { filterClusters } from "../../../store/filters/filtersManager";
import { FiltersActions } from "../../../store/filters/filtersSlice";
import CreateConstellation from "../../constellations/constellation/CreateConstellation";
import DeleteConstellation from "../../constellations/constellation/delete/DeleteConstellation";
import PairingCode from "../../pairingCode/PairingCode";
import ClustersCard from "../cards/ClustersCard";

interface IProps {
    constellation: IConstellationInfo;
    constellations: { [id: string]: IConstellationInfo };
    permissions: GcmUserPermission;
    clusterRemoteManagements: { [id: string]: ClusterRemoteManagement };
}

export default function ClustersTable(props: IProps) {
    const dispatch = useCarbonDispatch();
    const ZONE_ID = "clusters-table";

    const allClusterData: IClusterInfo[] = props.constellation.cluster;
    const crms = props.clusterRemoteManagements;

    const clusterView = useCarbonSelector(selectClusterView);

    // Filtering
    const clusterFilters = useCarbonSelector(selectClusterFilters);
    const searchInput = useCarbonSelector(selectSearchInput);
    const filteredClusters = filterClusters(clusterFilters, searchInput.clusters, allClusterData);

    const table = convertIClusterInfoToTable(filteredClusters);

    // Functions
    const onSearchBarChange = function (input: string) {
        dispatch(FiltersActions.setSearchInput({ ...searchInput, clusters: input }));
    };
    const insertBody = () => {
        if (allClusterData.length > 0) {
            if (filteredClusters.length > 0) {
                return (
                    <div className="constellationCardsWrapper">
                        {filteredClusters.map((c) => {
                            return (
                                <ClustersCard
                                    key={c.id.value}
                                    foundObject={{ cluster: c, constellation: props.constellation }}
                                    crm={{ value: crms[c.id.value] }}
                                    onRowClick={(id: StringID) => {
                                        dispatch(AppSliceActions.setAppView({ type: "CLUSTER", id: id }));
                                    }}
                                />
                            );
                        })}
                    </div>
                );
            } else {
                return (
                    <EmptyPlaceholder
                        img="./images/notFound.png"
                        title={"No Clusters Found"}
                        description={"Please refine your search/filter criteria and try again."}
                    />
                );
            }
        } else {
            return (
                <EmptyPlaceholder
                    img="./images/notFound.png"
                    title={"Add a Cluster"}
                    description={
                        "Pair a Userful Cluster to this Constellation. A Cluster is a group of connected servers running on the same network."
                    }
                    additionalComponents={
                        props.permissions.isClusterSetup ? (
                            <PairingCode type={"SECONDARY"} constellation={props.constellation} />
                        ) : null
                    }
                />
            );
        }
    };

    return (
        <div className="gcm-contents-wrapper">
            <div className="gcm-contents-header-wrapper">
                <div className="gcm-contents-sub-header-wrapper gcm-contents-sub-header-priority-wrapper">
                    <UserfulTooltip title="Back to Constellations">
                        <Button
                            variant="secondary"
                            iconOnly
                            onClick={(e) => {
                                e.preventDefault();

                                dispatch(AppSliceActions.setAppView({ type: "CONSTELLATIONS" }));
                            }}
                        >
                            <FontAwesomeIcon icon={faArrowLeft} />
                        </Button>
                    </UserfulTooltip>
                    <Dropdown>
                        <Dropdown.Toggle as={Button} bsPrefix="dropdownButtonSwitch" id="dropdownToggle">
                            <div className="textInside">{props.constellation.definition.name}</div>
                            <FontAwesomeIcon icon={faAngleDown} />
                        </Dropdown.Toggle>

                        <Dropdown.Menu
                            className="dropdownMenuSwitch"
                            onKeyDown={(e) => e.stopPropagation()}
                            onKeyUp={(e) => e.stopPropagation()}
                        >
                            {Object.values(props.constellations).length > 1 && (
                                <>
                                    {Object.values(props.constellations).map((value) => {
                                        if (value.definition.id.value !== props.constellation.definition.id.value) {
                                            return (
                                                <Dropdown.Item
                                                    className="dropdownItem"
                                                    key={value.definition.id.value}
                                                    onClick={() => {
                                                        dispatch(
                                                            AppSliceActions.setAppView({
                                                                type: "CONSTELLATION",
                                                                id: value.definition.id,
                                                            }),
                                                        );
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faSparkle} />
                                                    <div className="textInside">{value.definition.name}</div>
                                                </Dropdown.Item>
                                            );
                                        } else return null;
                                    })}
                                    <hr />
                                </>
                            )}
                            <CreateConstellation type="DROPDOWN" permissions={props.permissions} />
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
                <div className="gcm-contents-sub-header-wrapper">
                    <DeleteConstellation
                        type={"SECONDARY"}
                        constellations={{ [props.constellation.definition.id.value]: props.constellation }}
                    />
                </div>
            </div>
            <div className="gcm-contents-header-wrapper">
                <div className="gcm-contents-sub-header-wrapper gcm-contents-sub-header-priority-wrapper">
                    <UserfulSearchBar forceValue={searchInput.clusters} onChange={onSearchBarChange} />
                    <UserfulTooltip placement="bottom" title="Refresh Data">
                        <button
                            className="iconButton"
                            onClick={(e) => {
                                apiGetConstellations(getGlobalStates().keycloak.token);
                            }}
                        >
                            <FontAwesomeIcon icon={faRefresh} />
                        </button>
                    </UserfulTooltip>
                </div>
                <div className="gcm-contents-sub-header-wrapper">
                    <div className="multiStateWrapper">
                        <UserfulTooltip title={"Table View"}>
                            <div
                                className={`mSbutton ${clusterView === "TABLE" ? "active" : ""}`}
                                onClick={(e) => {
                                    dispatch(AppSliceActions.setClusterView("TABLE"));
                                }}
                            >
                                <FontAwesomeIcon icon={faBars} className="mSicon" />
                            </div>
                        </UserfulTooltip>

                        <UserfulTooltip title={"Card View"}>
                            <div
                                className={`mSbutton ${clusterView === "CARDS" ? "active" : ""}`}
                                onClick={(e) => {
                                    dispatch(AppSliceActions.setClusterView("CARDS"));
                                }}
                            >
                                <FontAwesomeIcon icon={faBorderAll} className="mSicon" />
                            </div>
                        </UserfulTooltip>
                    </div>
                    {props.permissions.isClusterSetup && (
                        <PairingCode type="PRIMARY" constellation={props.constellation} />
                    )}
                </div>
            </div>
            <div className="gcm-contents-body-wrapper">
                {clusterView === "TABLE" && allClusterData.length > 0 && (
                    <ClickAndDragZone id={ZONE_ID}>
                        {filteredClusters.length > 0 ? (
                            <UserfulTable
                                table={table}
                                selection={false}
                                onRowSingleClick={(row, index) => {
                                    const id = (row as any)?.id as StringID;
                                    if (id) {
                                        dispatch(AppSliceActions.setAppView({ type: "CLUSTER", id }));
                                    }
                                }}
                            />
                        ) : (
                            insertBody()
                        )}
                    </ClickAndDragZone>
                )}
                {clusterView === "TABLE" && allClusterData.length === 0 && insertBody()}
                {clusterView === "CARDS" && insertBody()}
            </div>
        </div>
    );
}
